import { createDeck, deal } from './deck';
import { scoreHand, scorePlayerRound, POINTS_TO_WIN } from './scoring';

const DEFAULT_BOT_DIFFICULTY = 'medium';

const createGameId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const getPlayerTotalCards = (state, playerIndex) => {
  const handCount = state.hands?.[playerIndex]?.length ?? 0;
  const pileCount = (state.handPiles?.[playerIndex] || []).reduce(
    (sum, pile) => sum + (Array.isArray(pile) ? pile.length : 0),
    0
  );
  return handCount + pileCount;
};

const buildPlayerNames = (numPlayers, names) => {
  const fallback = Array.from({ length: numPlayers }, (_, i) => `Player ${i + 1}`);
  if (!Array.isArray(names)) return fallback;
  return fallback.map((name, idx) => {
    const candidate = String(names[idx] ?? '').trim();
    return candidate ? candidate : name;
  });
};

export const PHASES = {
  LOBBY: 'lobby',
  PLAYING: 'playing',
  SURGE_GRACE: 'surgeGrace',
  ROUND_END: 'roundEnd',
  GAME_OVER: 'gameOver',
};

export const SURGE_GRACE_MS = 20000;

export function initialGameState(numPlayers = 2, options = {}) {
  const {
    playerNames,
    botDifficulty = DEFAULT_BOT_DIFFICULTY,
    roundNumber = 1,
    gameId = createGameId(),
  } = options;
  const normalizedNames = buildPlayerNames(numPlayers, playerNames);
  const deck = createDeck();
  const { hands } = deal(deck, numPlayers, 10);
  return {
    phase: PHASES.LOBBY,
    numPlayers,
    playerNames: normalizedNames,
    botDifficulty,
    roundNumber,
    gameId,
    hands,
    handPiles: Array.from({ length: numPlayers }, () => []),
    discardPile: null,
    startPlayerIndex: 0,
    currentPlayerIndex: 0,
    turnTimeLimitSec: null,
    turnDeadlineAt: null,
    lastPickedUpCardId: null,
    roundScores: Array(numPlayers).fill(0),
    roundDetails: null,
    gameScores: Array(numPlayers).fill(0),
    turnCount: 0,
    discardHistory: [],
    opponentPickups: Array.from({ length: numPlayers }, () => []),
    sergCalled: false,
    surgeGraceEndsAt: null,
    readyPlayers: null,
    equalizerUsed: Array(numPlayers).fill(false),
    roundWinner: null,
    gameWinner: null,
  };
}

export function startGame(numPlayers = 2, options = {}) {
  const {
    playerNames,
    botDifficulty = DEFAULT_BOT_DIFFICULTY,
    roundNumber = 1,
    gameId = createGameId(),
    prevGameScores,
    startPlayerIndex = 0,
    turnTimeLimitSec = null,
  } = options;
  const normalizedNames = buildPlayerNames(numPlayers, playerNames);
  const deck = createDeck();
  const { hands } = deal(deck, numPlayers, 10);
  const normalizedStartPlayer = ((startPlayerIndex % numPlayers) + numPlayers) % numPlayers;
  return {
    phase: PHASES.PLAYING,
    numPlayers,
    playerNames: normalizedNames,
    botDifficulty,
    roundNumber,
    gameId,
    hands,
    handPiles: Array.from({ length: numPlayers }, () => []),
    discardPile: null,
    startPlayerIndex: normalizedStartPlayer,
    currentPlayerIndex: normalizedStartPlayer,
    turnTimeLimitSec: Number.isFinite(turnTimeLimitSec) && turnTimeLimitSec > 0 ? Math.floor(turnTimeLimitSec) : null,
    turnDeadlineAt: null,
    lastPickedUpCardId: null,
    roundScores: Array(numPlayers).fill(0),
    roundDetails: null,
    gameScores: prevGameScores || Array(numPlayers).fill(0),
    turnCount: 0,
    discardHistory: [],
    opponentPickups: Array.from({ length: numPlayers }, () => []),
    sergCalled: false,
    surgeGraceEndsAt: null,
    readyPlayers: null,
    equalizerUsed: Array(numPlayers).fill(false),
    roundWinner: null,
    gameWinner: null,
  };
}

export function pickUpFromPile(state, playerIndex) {
  const isPlaying = state.phase === PHASES.PLAYING;
  const isSurgeGrace = state.phase === PHASES.SURGE_GRACE;
  if (!isPlaying && !isSurgeGrace) return state;
  if (isPlaying && playerIndex !== state.currentPlayerIndex) return state;
  if (!state.discardPile) return state;
  const hand = state.hands[playerIndex];
  if (!hand) return state;
  if (isSurgeGrace) {
    const someoneHasEleven = state.hands.some((_, i) => getPlayerTotalCards(state, i) === 11);
    // While anyone still holds 11, resolve that first (give one to the short player).
    if (someoneHasEleven) return state;
    const playerTotal = getPlayerTotalCards(state, playerIndex);
    const hasNine = state.hands.some((_, i) => getPlayerTotalCards(state, i) === 9);
    // Prefer the 9-card pickup; if no one has 9 (e.g. after equalizing), a 10 may take the centre card.
    if (playerTotal === 9) {
      // ok
    } else if (!hasNine && playerTotal === 10) {
      // ok
    } else {
      return state;
    }
  }
  const pickedCard = state.discardPile;
  const newHand = [...hand, pickedCard];
  const opponentPickups = (state.opponentPickups || []).map((arr, i) =>
    i === playerIndex ? [...arr, { rank: pickedCard.rank, suit: pickedCard.suit }] : arr
  );
  return {
    ...state,
    hands: state.hands.map((h, i) => (i === playerIndex ? newHand : h)),
    discardPile: null,
    lastPickedUpCardId: pickedCard.id,
    opponentPickups,
  };
}

/**
 * SURGE grace: a player with 11 cards must give one card to a player with 9 (balances both to 10).
 * The centre pile is unchanged; usual rules apply (cannot give the card you just picked up).
 */
export function surgeGraceEqualizeDiscard(state, playerIndex, card) {
  if (state.phase !== PHASES.SURGE_GRACE) return state;
  const hand = state.hands[playerIndex];
  if (!hand || getPlayerTotalCards(state, playerIndex) !== 11) return state;
  if (state.lastPickedUpCardId === card.id) return state;
  const cardInHand = hand.find((c) => c.id === card.id);
  if (!cardInHand) return state;
  const receiverIdx = state.hands.findIndex(
    (_, i) => i !== playerIndex && getPlayerTotalCards(state, i) === 9
  );
  if (receiverIdx < 0) return state;
  const newFrom = hand.filter((c) => c.id !== card.id);
  const newReceiver = [...state.hands[receiverIdx], card];
  return {
    ...state,
    hands: state.hands.map((h, i) => {
      if (i === playerIndex) return newFrom;
      if (i === receiverIdx) return newReceiver;
      return h;
    }),
    lastPickedUpCardId: null,
  };
}

export function discardCard(state, playerIndex, card) {
  if (state.phase !== PHASES.PLAYING) return state;
  if (playerIndex !== state.currentPlayerIndex) return state;
  if (state.lastPickedUpCardId === card.id) return state;
  const hand = state.hands[playerIndex];
  const cardInHand = hand.find((c) => c.id === card.id);
  if (!cardInHand) return state;
  const newHands = state.hands.map((h, i) =>
    i === playerIndex ? h.filter((c) => c.id !== card.id) : h
  );
  const nextPlayer = (state.currentPlayerIndex + 1) % state.numPlayers;

  return {
    ...state,
    hands: newHands,
    discardPile: card,
    currentPlayerIndex: nextPlayer,
    lastPickedUpCardId: null,
    turnCount: (state.turnCount || 0) + 1,
    discardHistory: [...(state.discardHistory || []), { rank: card.rank, suit: card.suit, by: playerIndex }],
  };
}

export function pickUpAndDiscard(state, playerIndex, cardToDiscard) {
  if (state.phase !== PHASES.PLAYING) return state;
  if (playerIndex !== state.currentPlayerIndex) return state;
  if (!state.discardPile) return state;
  const hand = state.hands[playerIndex];
  if (cardToDiscard.id === state.discardPile.id) return state;
  const cardInHand = hand.find((c) => c.id === cardToDiscard.id);
  if (!cardInHand) return state;

  const newHand = [...hand.filter((c) => c.id !== cardToDiscard.id), state.discardPile];
  const nextPlayer = (playerIndex + 1) % state.numPlayers;

  const opponentPickups = (state.opponentPickups || []).map((arr, i) =>
    i === playerIndex ? [...arr, { rank: state.discardPile.rank, suit: state.discardPile.suit }] : arr
  );
  return {
    ...state,
    hands: state.hands.map((h, i) => (i === playerIndex ? newHand : h)),
    discardPile: cardToDiscard,
    currentPlayerIndex: nextPlayer,
    lastPickedUpCardId: null,
    turnCount: (state.turnCount || 0) + 1,
    discardHistory: [...(state.discardHistory || []), { rank: cardToDiscard.rank, suit: cardToDiscard.suit, by: playerIndex }],
    opponentPickups,
  };
}

const ensurePiles = (state) =>
  state.handPiles || Array.from({ length: state.numPlayers }, () => []);

/** Move cards from hand into a new pile (player 0 only during play). */
export function createPile(state, playerIndex, cardIds) {
  if (state.phase !== PHASES.PLAYING && state.phase !== PHASES.SURGE_GRACE) return state;
  if (!cardIds || cardIds.length < 2) return state;
  const hand = state.hands[playerIndex];
  if (!hand) return state;
  const cards = cardIds.map((id) => hand.find((c) => c.id === id)).filter(Boolean);
  if (cards.length !== cardIds.length) return state;
  const handPiles = ensurePiles(state);
  const newHand = hand.filter((c) => !cardIds.includes(c.id));
  const newPiles = [...(handPiles[playerIndex] || []), cards];
  return {
    ...state,
    hands: state.hands.map((h, i) => (i === playerIndex ? newHand : h)),
    handPiles: handPiles.map((p, i) => (i === playerIndex ? newPiles : p)),
  };
}

/** Add one or more cards from hand onto an existing pile (player 0 during play). */
export function addCardsToPile(state, playerIndex, pileIndex, cardIds) {
  if (state.phase !== PHASES.PLAYING && state.phase !== PHASES.SURGE_GRACE) return state;
  if (!cardIds || cardIds.length < 1) return state;
  const handPiles = ensurePiles(state);
  const piles = handPiles[playerIndex] || [];
  const pile = piles[pileIndex];
  if (!pile) return state;
  const hand = state.hands[playerIndex];
  if (!hand) return state;
  const cards = cardIds.map((id) => hand.find((c) => c.id === id)).filter(Boolean);
  if (cards.length !== cardIds.length) return state;
  const newPile = [...pile, ...cards];
  const newHand = hand.filter((c) => !cardIds.includes(c.id));
  const newPiles = piles.map((p, i) => (i === pileIndex ? newPile : p));
  return {
    ...state,
    hands: state.hands.map((h, i) => (i === playerIndex ? newHand : h)),
    handPiles: handPiles.map((p, i) => (i === playerIndex ? newPiles : p)),
  };
}

/** Return all cards from a pile back to the player's hand. */
export function breakPile(state, playerIndex, pileIndex) {
  if (state.phase !== PHASES.PLAYING && state.phase !== PHASES.SURGE_GRACE) return state;
  const handPiles = ensurePiles(state);
  const piles = handPiles[playerIndex];
  if (!piles || !piles[pileIndex]) return state;
  const cards = piles[pileIndex];
  const newHand = [...state.hands[playerIndex], ...cards];
  const newPiles = piles.filter((_, i) => i !== pileIndex);
  return {
    ...state,
    hands: state.hands.map((h, i) => (i === playerIndex ? newHand : h)),
    handPiles: handPiles.map((p, i) => (i === playerIndex ? newPiles : p)),
  };
}

/** Return a single card from a pile back to the player's hand. */
export function returnCardFromPile(state, playerIndex, pileIndex, cardId) {
  if (state.phase !== PHASES.PLAYING && state.phase !== PHASES.SURGE_GRACE) return state;
  const handPiles = ensurePiles(state);
  const piles = handPiles[playerIndex];
  if (!piles || !piles[pileIndex]) return state;
  const pile = piles[pileIndex];
  const card = pile.find((c) => c.id === cardId);
  if (!card) return state;
  const newPile = pile.filter((c) => c.id !== cardId);
  const newPiles =
    newPile.length > 0
      ? piles.map((p, i) => (i === pileIndex ? newPile : p))
      : piles.filter((_, i) => i !== pileIndex);
  const newHand = [...state.hands[playerIndex], card];
  return {
    ...state,
    hands: state.hands.map((h, i) => (i === playerIndex ? newHand : h)),
    handPiles: handPiles.map((p, i) => (i === playerIndex ? newPiles : p)),
  };
}

export function callSerg(state) {
  if (state.phase !== PHASES.PLAYING) return state;
  const readyPlayers = Array.from({ length: state.numPlayers }, () => false);
  return {
    ...state,
    sergCalled: true,
    phase: PHASES.SURGE_GRACE,
    surgeGraceEndsAt: Date.now() + SURGE_GRACE_MS,
    readyPlayers,
  };
}

export function setSurgeReady(state, playerIndex) {
  if (state.phase !== PHASES.SURGE_GRACE) return state;
  if (!Array.isArray(state.readyPlayers)) return state;
  if (playerIndex < 0 || playerIndex >= state.readyPlayers.length) return state;
  if (state.readyPlayers[playerIndex]) return state;
  const readyPlayers = [...state.readyPlayers];
  readyPlayers[playerIndex] = true;
  return {
    ...state,
    readyPlayers,
  };
}

export function finalizeSerg(state) {
  if (state.phase !== PHASES.SURGE_GRACE) return state;
  const handPiles = ensurePiles(state);
  let scoredHands = state.hands;
  let scoredDiscardPile = state.discardPile;

  // If grace ended before a player with 11 resolved, move one legal card to a 9-card player.
  let giverIdx = scoredHands.findIndex((_, i) => getPlayerTotalCards({ ...state, hands: scoredHands }, i) === 11);
  let recvIdx = scoredHands.findIndex((_, i) => getPlayerTotalCards({ ...state, hands: scoredHands }, i) === 9);
  if (giverIdx >= 0 && recvIdx >= 0 && giverIdx !== recvIdx) {
    const gh = scoredHands[giverIdx];
    const toMove = gh.find((c) => c.id !== state.lastPickedUpCardId) ?? gh[0];
    if (toMove) {
      scoredHands = scoredHands.map((h, i) => {
        if (i === giverIdx) return h.filter((c) => c.id !== toMove.id);
        if (i === recvIdx) return [...h, toMove];
        return h;
      });
    }
  }

  // Rule: rounds are scored as 10-card hands. If a discard is still on table,
  // give it to the player currently holding 9 cards before scoring.
  if (scoredDiscardPile) {
    const receiver = scoredHands.findIndex((_, i) => getPlayerTotalCards({ ...state, hands: scoredHands }, i) === 9);
    if (receiver >= 0) {
      scoredHands = scoredHands.map((h, i) => (i === receiver ? [...h, scoredDiscardPile] : h));
      scoredDiscardPile = null;
    }
  }

  const roundDetails = scoredHands.map((hand, i) => {
    const owesUnpickedDiscard =
      scoredDiscardPile != null && state.currentPlayerIndex === i;
    const unpickedDiscardPenalty = owesUnpickedDiscard ? 1 : 0;
    if (i === 0) {
      return scorePlayerRound(handPiles[i], hand, { unpickedDiscardPenalty });
    }
    const allCards = [...hand, ...(handPiles[i] || []).flat()];
    const autoScore = scoreHand(allCards);
    const pileScores = (autoScore.breakdown || []).map((group) => ({
      points: group.points,
      type: group.type || 'valid',
      cards: group.cards || [],
    }));
    const groupedPoints = pileScores.reduce((sum, p) => sum + (p.points || 0), 0);
    const shellBonus = autoScore.bonus || 0;
    const totalPenalty = (autoScore.unassigned || 0) + unpickedDiscardPenalty;
    const total = groupedPoints - totalPenalty + shellBonus;
    return {
      total,
      pileScores,
      penalty: totalPenalty,
      roundShellBonus: shellBonus,
      maxPotential: autoScore.points,
      missedPotential: Math.max(0, autoScore.points - total),
    };
  });

  const roundScores = roundDetails.map((d) => d.total);
  const gameScores = state.gameScores.map((s, i) => s + roundScores[i]);
  const instantWinner = roundDetails.findIndex((d) => d?.instantWin);
  const maxGame = Math.max(...gameScores);
  const gameWinner =
    instantWinner >= 0
      ? instantWinner
      : gameScores.some((s) => s >= POINTS_TO_WIN) ? gameScores.indexOf(maxGame) : null;

  return {
    ...state,
    phase: gameWinner !== null ? PHASES.GAME_OVER : PHASES.ROUND_END,
    roundScores,
    roundDetails,
    gameScores,
    roundWinner: null,
    gameWinner,
    hands: scoredHands,
    discardPile: null,
    surgeGraceEndsAt: null,
    readyPlayers: null,
  };
}

export function nextRound(state) {
  if (state.phase !== PHASES.ROUND_END) return state;
  const nextStartPlayer = ((state.startPlayerIndex ?? 0) + 1) % state.numPlayers;
  return startGame(state.numPlayers, {
    playerNames: state.playerNames,
    botDifficulty: state.botDifficulty,
    roundNumber: (state.roundNumber ?? 1) + 1,
    gameId: state.gameId,
    prevGameScores: state.gameScores,
    startPlayerIndex: nextStartPlayer,
    turnTimeLimitSec: state.turnTimeLimitSec ?? null,
  });
}

export function reorderHand(state, playerIndex, fromIndex, toIndex) {
  const hand = state.hands[playerIndex];
  if (!hand || fromIndex < 0 || toIndex < 0 || fromIndex >= hand.length || toIndex >= hand.length) {
    return state;
  }
  const newHand = [...hand];
  const [moved] = newHand.splice(fromIndex, 1);
  newHand.splice(toIndex, 0, moved);
  return {
    ...state,
    hands: state.hands.map((h, i) => (i === playerIndex ? newHand : h)),
  };
}
